import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import JSZip from 'jszip';
import { blobToBase64 } from '../../../backend/storageHelpers';

export interface ExportOptions {
    format: 'gif' | 'mp4' | 'png' | 'zip' | 'webm';
    fps?: number;
    rows?: number;
    cols?: number;
    sheetWidth?: number;
    sheetHeight?: number;
}

/**
 * Loads the FFmpeg instance if not already loaded
 * Prioritizes multithreaded core if headers are present
 */
async function loadFFmpeg(ffmpeg: FFmpeg) {
    if (!ffmpeg.loaded) {
        const isMultithreaded = typeof window !== 'undefined' && window.crossOriginIsolated;
        const coreVersion = isMultithreaded ? 'core-mt' : 'core';
        const baseURL = `https://unpkg.com/@ffmpeg/${coreVersion}@0.12.6/dist/umd`;

        // console.log(`Loading FFmpeg (${isMultithreaded ? 'Multi-threaded' : 'Single-threaded'})...`);

        await ffmpeg.load({
            coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
            wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
            ...(isMultithreaded && {
                workerURL: await toBlobURL(`${baseURL}/ffmpeg-core.worker.js`, 'text/javascript'),
            }),
        });
    }
}

/**
 * Writes the frame images to the FFmpeg virtual file system
 */
async function writeFramesToFFmpeg(ffmpeg: FFmpeg, images: string[], prefix: string = 'frame') {
    for (let i = 0; i < images.length; i++) {
        const filename = `${prefix}_${i.toString().padStart(4, '0')}.png`;
        await ffmpeg.writeFile(filename, await fetchFile(images[i]));
    }
}

/**
 * Handles exporting base64 frames to various formats
 */
export async function exportAnimation(images: string[], options: ExportOptions): Promise<Blob> {
    const { format, fps = 24 } = options;

    if (format === 'zip') {
        return exportFramesAsZip(images);
    }

    const ffmpeg = new FFmpeg();
    
    try {
        await loadFFmpeg(ffmpeg);
        await writeFramesToFFmpeg(ffmpeg, images);

        if (format === 'gif') {
            return await generateGif(ffmpeg, images.length, fps);
        } else if (format === 'mp4') {
            return await generateVideo(ffmpeg, images.length, fps);
        } else if (format === 'png') {
            return await generateSpriteSheet(ffmpeg, images.length, options);
        } else if (format === 'webm') {
            return await generateWebM(ffmpeg, images.length, fps);
        }
        
        throw new Error(`Unsupported export format: ${format}`);
    } finally {
        ffmpeg.terminate();
    }
}

async function exportFramesAsZip(images: string[]): Promise<Blob> {
    const zip = new JSZip();
    
    for (let i = 0; i < images.length; i++) {
        const fileData = await fetchFile(images[i]);
        const filename = `frame_${(i + 1).toString().padStart(4, '0')}.png`;
        zip.file(filename, fileData); 
    }
    
    return await zip.generateAsync({ type: 'blob' });
}

async function generateGif(ffmpeg: FFmpeg, count: number, fps: number): Promise<Blob> {
    // 1. Generate palette with reserve_transparent=1
    // We use a simpler filter here to ensure the palette specifically recognizes the alpha channel
    await ffmpeg.exec([
        '-framerate', '24', // Intentionally hardcoded to 24 FPS
        '-i', 'frame_%04d.png',
        '-vf', 'palettegen=reserve_transparent=1',
        'palette.png'
    ]);

    // 2. Use paletteuse with alpha_threshold
    // transparency_color is a fallback for older decoders but helps force the issue
    await ffmpeg.exec([
        '-framerate', '24', // Intentionally hardcoded to 24 FPS
        '-i', 'frame_%04d.png',
        '-i', 'palette.png',
        '-filter_complex', '[0:v][1:v]paletteuse=alpha_threshold=128',
        '-gifflags', '-offsetting',
        'output.gif'
    ]);

    const data = await ffmpeg.readFile('output.gif');
    return new Blob([data as any], { type: 'image/gif' });
}

async function generateVideo(ffmpeg: FFmpeg, count: number, fps: number): Promise<Blob> {
    await ffmpeg.exec([
        '-framerate', fps.toString(),
        '-i', 'frame_%04d.png',
        '-c:v', 'libx264',
        '-pix_fmt', 'yuv420p',
        'output.mp4'
    ]);

    const data = await ffmpeg.readFile('output.mp4');
    return new Blob([data as any], { type: 'video/mp4' });
}

async function generateSpriteSheet(ffmpeg: FFmpeg, count: number, options: ExportOptions): Promise<Blob> {
    const { 
        rows = 1, 
        cols = count,
        sheetWidth,
        sheetHeight
    } = options;
    
    // Explicitly set the background color to transparent for the tile filter
    // and specify the output pixel format as rgba
    let vf = `format=rgba,tile=${cols}x${rows}:color=0x00000000`;

    if (sheetWidth || sheetHeight) {
        const w = sheetWidth ? `${sheetWidth}/${cols}` : '-1';
        const h = sheetHeight ? `${sheetHeight}/${rows}` : '-1';
        vf = `format=rgba,scale=${w}:${h},${vf}`;
    }

    await ffmpeg.exec([
        '-i', 'frame_%04d.png',
        '-frames:v', count.toString(),
        '-vf', vf,
        '-pix_fmt', 'rgba',
        'output.png'
    ]);

    const data = await ffmpeg.readFile('output.png');
    return new Blob([data as any], { type: 'image/png' });
}

async function generateWebM(ffmpeg: FFmpeg, count: number, fps: number): Promise<Blob> {
    await ffmpeg.exec([
        '-framerate', fps.toString(),
        '-i', 'frame_%04d.png',
        '-c:v', 'libvpx-vp9',
        '-lossless', '1',
        '-pix_fmt', 'yuva444p',
        'output.webm'
    ]);

    const data = await ffmpeg.readFile('output.webm');
    return new Blob([data as any], { type: 'video/webm' });
}